import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { post } from '../api/api';
import { showToast } from '../components/utils/toast';
import { INote } from '../interface/notes';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import {
  PreliminaryInvoiceCreationRequest,
  PreliminaryInvoiceCreationType,
} from '../interface/invoice';

type CreatePreliminaryInvoiceWithParams = PreliminaryInvoiceCreationRequest & {
  id: string;
};

const buildCreationRequest = (params: CreatePreliminaryInvoiceWithParams) => {
  if (params.type === PreliminaryInvoiceCreationType.PERIODICAL) {
    return { url: API_URLS.CREATE_PRELIMINARY_INVOICE_PERIODICAL, body: {} };
  }
  return {
    url: API_URLS.CREATE_PRELIMINARY_INVOICE_UNTIL_DATE,
    body: { entryDate: params.entryDate, periodEndDate: params.periodEndDate },
  };
};

const createPreliminaryInvoice = async (
  params: CreatePreliminaryInvoiceWithParams,
): Promise<INote> => {
  const { url, body } = buildCreationRequest(params);
  return post(url.replace(':id', params.id), body, {
    skipGlobalErrorHandler: true,
  });
};

export const useCreatePreliminaryInvoice = (): UseMutationResult<
  INote,
  AxiosError<{ message?: string; type?: string }>,
  CreatePreliminaryInvoiceWithParams
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: createPreliminaryInvoice,
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.PRELIMINARY_INVOICE_CREATED);
      showToast('success', t('matter:preliminaryInvoiceCreated'));
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
